import { useEffect, useState, useCallback, useRef } from "react";

const INITIAL_ITEMS = 30;
const ITEMS_PER_LOAD = 20;

interface UseLazyFacetItemsProps {
  allItems: string[];
}

export const useLazyFacetItems = ({ allItems }: UseLazyFacetItemsProps) => {
  const [displayedItemsCount, setDisplayedItemsCount] = useState(INITIAL_ITEMS);
  const [previousAllItems, setPreviousAllItems] = useState(allItems);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Reset when allItems changes
  if (allItems !== previousAllItems) {
    setPreviousAllItems(allItems);
    setDisplayedItemsCount(INITIAL_ITEMS);
  }

  const displayedItems = allItems.slice(0, displayedItemsCount);
  const hasMore = displayedItemsCount < allItems.length;

  const loadMore = useCallback(() => {
    if (hasMore) {
      setDisplayedItemsCount((prev) =>
        Math.min(prev + ITEMS_PER_LOAD, allItems.length),
      );
    }
  }, [hasMore, allItems.length]);

  // Scroll detection for lazy loading
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const threshold = 50; // Load more when 50px from bottom

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMore();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  return {
    displayedItems,
    hasMore,
    scrollContainerRef,
  };
};
